// ─── Types ────────────────────────────────────────────────────────────────────

// View-model used in UI (derived from TImportLogRes or mock)
export interface TImportLog {
  importYearMonth: string | null; // YYYY/MM display
  importDatetime: string | null; // YYYY-MM-DD HH:mm:ss display
  recordCount: number | null;
  status: 'COMPLETE' | 'SUCCESS' | 'ERROR' | 'FILE_ERRORS' | null;
}

// Converts BE status string → UI status
export function toImportStatus(
  beStatus: string | undefined
): 'COMPLETE' | 'ERROR' | null {
  if (!beStatus) {
    return null;
  }
  return beStatus === 'COMPLETED' || beStatus === 'SUCCESS'
    ? 'COMPLETE'
    : 'ERROR';
}

// Formats yyyyMM → YYYY/MM (e.g. "202603" → "2026/03")
export function formatYearMonth(
  yyyyMM: string | null | undefined
): string | null {
  if (!yyyyMM || yyyyMM.length !== 6) {
    return null;
  }
  return `${yyyyMM.slice(0, 4)}/${yyyyMM.slice(4, 6)}`;
}

// Formats ISO datetime → YYYY-MM-DD HH:mm:ss in local timezone
export function formatImportDatetime(
  iso: string | null | undefined
): string | null {
  if (!iso) {
    return null;
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) {
    return null;
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

// Converts "YYYY/MM" → "yyyyMM" for API request (e.g. "2026/03" → "202603")
export function yearMonthToApiFormat(displayValue: string): string {
  return displayValue.replace('/', '');
}

// Returns true if the actual inventory session month is February (02) or August (08).
// These sessions hold the data that must be displayed to the user as March/September.
export function isFebruaryOrAugust(yyyyMM: string): boolean {
  const mm = yyyyMM.slice(4, 6);
  return mm === '02' || mm === '08';
}

// Formats the actual session yyyyMM (Feb/Aug) as the display month shown to the
// customer, which is one month later (Mar/Sep) — e.g. "202502" → "2025/03"
export function formatYearMonthDisplayShifted(yyyyMM: string): string {
  const year = Number(yyyyMM.slice(0, 4));
  const month = Number(yyyyMM.slice(4, 6)) + 1;
  return `${year}/${String(month).padStart(2, '0')}`;
}

export interface TInventoryYearMonthOption {
  value: string; // YYYY/MM
  label: string; // YYYY/MM
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const NX_INTEGRATION_CONSTANT = {
  TITLE: {
    PAGE: 'NX連携',
    TAB_LEDGER: '固定資産台帳',
    TAB_DISPOSAL: '除却出力',
    BTN_CSV_OUTPUT: 'CSV出力',
    OUTPUT_CONFIRM_TITLE: '出力確認',
    BTN_CANCEL: 'キャンセル',
    BTN_OK: 'OK',
  },
  LABEL: {
    IMPORT_YEAR_MONTH: '取込年月:',
    IMPORT_DATETIME: '取込日時:',
    IMPORT_COUNT: '取込件数:',
    STATUS: 'ステータス:',
    STATUS_COMPLETE: '✅ 完了',
    STATUS_ERROR: '❌ エラー',
    INVENTORY_YEAR_MONTH: '棚卸年月:',
  },
  NOTE: {
    AUTO_IMPORT:
      '※ 固定資産台帳のインポートは自動バッチ処理により実行されます。Superstream-NX担当者がVAMSのS3に4種類のCSVファイルをアップロードすると、VAMSシステムが自動的にデータベースへ取り込みます。手動操作は不要です。',
    YEAR_MONTH_RESTRICTION: '※ 3月または9月のみ選択可能',
  },
  DIALOG: {
    MESSAGE: '以下の条件でCSVを出力してもよろしいですか？',
    LABEL_OUTPUT_TYPE: '出力タイプ',
    OUTPUT_TYPE_VALUE: '除却出力 (未使用資産)',
    LABEL_YEAR_MONTH: '棚卸年月',
    LABEL_EXPORT_TARGET: '出力対象',
    EXPORT_TARGET_VALUE: '全店舗',
  },
  MESSAGE: {
    S01: 'CSVの出力が完了しました。',
    I01: '対象データが存在しません。棚卸年月を確認してください。',
    E02: 'CSV出力に失敗しました。再度お試しください。',
  },
} as const;
